import { parseNumericValue } from "./common";
import { toDecimal } from "./decimal-utils";

export const numberFormat = (value, skipSymbol = false) => {
  const parsedValue = parseFloat(value) || 0;
  const isNegative = parsedValue < 0;

  const numberToFormat = isNegative ? Math.abs(parsedValue) : parsedValue;

  const formatedValue = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numberToFormat);

  return skipSymbol
    ? (isNegative ? "-" : "") + formatedValue
    : (isNegative ? "-$" : "$") + formatedValue;
};

export const _numberFormat = ({ value = 0, decimal = 2 }) => {
  const newValue = new Intl.NumberFormat("en-US", {
    style: "decimal",
    roundingMode: "trunc",
    maximumFractionDigits: decimal,
  }).format(value || 0);

  const parseToFloat = parseNumericValue(newValue);
  return parseToFloat;
};

export const toUSD = (value) => {
  // Convert to Decimal and handle null/undefined by defaulting to 0
  const decimalValue = toDecimal(value || 0);

  // Format the value as USD without rounding
  // Note: The formatter will handle the display formatting, but we pass the precise Decimal value
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    // Prevent any rounding in the formatter
    minimumFractionDigits: 2,
    maximumFractionDigits: 2, // High value to preserve all decimal places
    useGrouping: true,
  }).format(decimalValue);
};

/**
 * Calculates percentage and formats it as a localized percentage string
 * Uses Decimal.js for precise calculations
 *
 * @param {number|string} valueA - Numerator value
 * @param {number|string} valueB - Denominator value
 * @returns {string} Formatted percentage string (e.g., "25.00%")
 *
 * @example
 * // Returns "25.00%"
 * toPercent(25, 100);
 *
 * @example
 * // Returns "100.00%"
 * toPercent(50, 0); // When denominator is 0 and numerator is not
 */
export const toPercent = (valueA = 0, valueB = 0) => {
  const _valueA = toDecimal(valueA);
  const _valueB = toDecimal(valueB);

  let percentage;

  if (!_valueB.isZero()) {
    percentage = _valueA.dividedBy(_valueB);
  } else if (!_valueA.isZero()) {
    percentage = toDecimal(1); // 100%
  } else {
    percentage = toDecimal(0);
  }

  // Convert to number for Intl.NumberFormat
  const percentageValue = percentage.toNumber() * 100;

  return Intl.NumberFormat("en-GB", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(percentageValue / 100);
};

export const showPercent = (value = 0) => {
  value = value / 100;
  return Intl.NumberFormat("en-GB", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
};

export const parseCurrencyValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  // If it's already a number, return it
  if (typeof value === "number") {
    return value;
  }

  // Convert to string and remove currency symbols and commas
  const cleanValue = String(value)
    .replace(/[$,]/g, "") // Remove $ and commas
    .trim();

  const parsedValue = parseFloat(cleanValue);
  return isNaN(parsedValue) ? 0 : parsedValue;
};
